import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';
import {
  IntakeFormQuickPlanForm,
  QuickPlanForm,
  QuickPlanModeSelector,
  useShortFormIntake,
  type QuickPlanFormValues,
  type QuickPlanMode,
} from '@/features/client-quick-plan';
import { AgentEnteredWaitingCard, agentEnteredIntakeApi } from '@/features/invite-client';
import { usePilotAgentEnteredIntake } from '@/shared/hooks/use-active-pilot-program';
import { toDateOnlyString } from '@/shared/lib/utils';
import { useQuickPlanAvailability } from '../hooks/use-quick-plan-availability';
import {
  useQuickPlanDialogState,
  type QuickPlanConsentStep,
} from '../hooks/use-quick-plan-dialog-state';
import { EdjQuickPlanForm } from './edj-quick-plan-form';

import { ArrowLeft } from 'lucide-react';

interface QuickPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  advisorId: string;
  onSubmitSuccess?: (data: ShortFormIntakeResponse) => void;
  // Open straight into a specific mode instead of showing the mode selector.
  initialMode?: QuickPlanMode;
  // When true, the mode selector and Back button are suppressed (single-mode dialog).
  lockMode?: boolean;
}

const EDJ_QUICK_PLAN_COPY = {
  title: 'New Case Quick Start',
  description: 'Enter a few details to create a quick estimate for your client.',
};

/** The single form most advisors get: a plan from the basics, and optionally the questionnaire. */
const SINGLE_FORM_COPY = {
  title: 'New Case Quick Start',
  description:
    'Enter basic client info to create a plan instantly. You can also email the client the medical questionnaire.',
};

function getDialogCopy(
  mode: QuickPlanMode | null,
  consentStep?: QuickPlanConsentStep,
  consentAccepted = false,
  clientFirstName?: string,
) {
  // Both options collect the same details for an agent-entered advisor, so they share the
  // consent copy and differ only in what happens at the end.
  if (consentStep === 'identity') {
    return {
      title: mode === 'intake-form' ? 'Send Medical Intake Form' : 'New Case Quick Start',
      description:
        "Enter your client's details. We'll email them for permission before collecting anything else.",
    };
  }
  if (consentStep === 'waiting') {
    if (consentAccepted && clientFirstName) {
      return {
        title: 'Permission received',
        description: `${clientFirstName} has given you permission to continue.`,
      };
    }

    return {
      title: 'Waiting on your client',
      description: 'They need to give permission before you can continue.',
    };
  }
  if (consentStep === 'health') {
    return {
      title: mode === 'intake-form' ? 'Send Medical Intake Form' : 'New Case Quick Start',
      description:
        mode === 'intake-form'
          ? 'Permission received. Add the remaining details to create the plan and send the intake form.'
          : 'Permission received. Add the remaining details to create the plan.',
    };
  }

  switch (mode) {
    case 'quick-plan':
      return {
        title: 'New Case Quick Start',
        description: 'Enter basic client info to create a plan instantly. No email invite needed.',
      };
    case 'intake-form':
      return {
        title: 'Send Medical Intake Form',
        description: 'Enter basic info and email to generate a plan and send the intake form.',
      };
    default:
      return {
        title: 'New Case Quick Start',
        description: "Choose how you'd like to create this plan.",
      };
  }
}

export function QuickPlanDialog({
  open,
  onOpenChange,
  advisorId,
  onSubmitSuccess,
  initialMode,
  lockMode = false,
}: QuickPlanDialogProps) {
  const isMobile = useIsMobile();
  // An EDJ advisor with `edjQuickPlan` gets only the EDJ form: no mode choice, partner or consent step.
  const { isEdjQuickPlan } = useQuickPlanAvailability();
  const isAgentEntered = !!usePilotAgentEnteredIntake();

  // One form with a tick box for sending the questionnaire, instead of choosing between two
  // options up front. Not for agent-entered advisors, whose two options both begin with a consent
  // request, nor for a dialog locked to one mode.
  const isSingleForm = !isAgentEntered && !lockMode;

  const {
    mode,
    setMode,
    showPartnerDialog,
    setShowPartnerDialog,
    consentStep,
    setConsentStep,
    consentAccepted,
    setConsentAccepted,
    identityValues,
    setIdentityValues,
    agentEnteredIntakeId,
    setAgentEnteredIntakeId,
    resetConsentFlow,
  } = useQuickPlanDialogState({ open, initialMode });

  // For an agent-entered advisor both options collect the same details and both require the
  // client's permission; the only difference left is whether the questionnaire is also sent.
  const sendIntakeForm = mode === 'intake-form';

  const { submitQuickPlan, isPending } = useShortFormIntake({
    advisorId,
    onSuccess: onSubmitSuccess,
    sendIntakeForm,
  });

  const requestConsent = useMutation({
    mutationFn: agentEnteredIntakeApi.create,
    onSuccess: (data, variables) => {
      setAgentEnteredIntakeId(data.agentEnteredIntakeId);
      setConsentStep('waiting');

      if (data.reused) return;

      if (data.status === 'undeliverable') {
        toast.error('We couldn’t email that address', {
          description: 'It has unsubscribed from Waterlily emails. Try another address.',
        });
        return;
      }

      if (data.status === 'send-failed') {
        toast.error('We couldn’t send that email', {
          description: 'Nothing reached your client. Try sending it again.',
        });
        return;
      }

      toast.success('Permission request sent', {
        description: `We emailed ${data.sentTo}. ${variables.firstName} needs to accept before you can continue.`,
      });
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to send the permission request.',
      );
    },
  });

  function handleIdentitySubmit(data: QuickPlanFormValues) {
    setIdentityValues(data);
    requestConsent.mutate({
      advisorId,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: (data.email ?? '').trim(),
      birthDate: toDateOnlyString(data.birthDate),
      intakeMode: 'short-form',
    });
  }

  function handleSubmit(data: QuickPlanFormValues) {
    if (!isAgentEntered) {
      submitQuickPlan(data);
      return;
    }

    // The health step's form only knows the health answers, so the identity the customer
    // actually consented to is merged back in here rather than re-asked.
    submitQuickPlan({
      ...identityValues,
      ...data,
      agentEnteredIntakeId: agentEnteredIntakeId ?? undefined,
    } as QuickPlanFormValues);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setMode(initialMode ?? null);
      resetConsentFlow();
    }

    onOpenChange(nextOpen);
  }

  function handleDone(data?: ShortFormIntakeResponse) {
    setMode(initialMode ?? null);
    onOpenChange(false);
    onSubmitSuccess?.(data ?? ({} as ShortFormIntakeResponse));
  }

  const { title, description } = isEdjQuickPlan
    ? EDJ_QUICK_PLAN_COPY
    : isSingleForm
      ? SINGLE_FORM_COPY
      : getDialogCopy(
          mode,
          isAgentEntered && mode !== null ? consentStep : undefined,
          consentAccepted,
          identityValues?.firstName,
        );

  const content = isEdjQuickPlan ? (
    <EdjQuickPlanForm
      advisorId={advisorId}
      onSubmitSuccess={onSubmitSuccess}
    />
  ) : (
    <>
      {mode !== null && !lockMode && !isSingleForm && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('w-fit', {
            'mb-6': isMobile,
          })}
          onClick={() => setMode(null)}
        >
          <ArrowLeft />
          Back
        </Button>
      )}

      {mode === null && !isSingleForm && <QuickPlanModeSelector onSelectMode={setMode} />}

      {isSingleForm && (
        <IntakeFormQuickPlanForm
          advisorId={advisorId}
          onDone={handleDone}
          onPartnerDialogChange={setShowPartnerDialog}
        />
      )}

      {/* A dialog locked to sending the form gets the same form, with the box already ticked. */}
      {mode === 'intake-form' && !isAgentEntered && !isSingleForm && (
        <IntakeFormQuickPlanForm
          advisorId={advisorId}
          onDone={handleDone}
          onPartnerDialogChange={setShowPartnerDialog}
          initialSendIntakeForm
        />
      )}

      {mode === 'quick-plan' && !isAgentEntered && !isSingleForm && (
        <QuickPlanForm
          onSubmit={handleSubmit}
          isSubmitting={isPending}
          variant="dialog"
          onPartnerDialogChange={setShowPartnerDialog}
        />
      )}

      {mode !== null && isAgentEntered && consentStep === 'identity' && (
        <QuickPlanForm
          step="identity"
          showEmail
          submitLabel="Request Client Consent"
          onSubmit={handleIdentitySubmit}
          isSubmitting={requestConsent.isPending}
          variant="dialog"
        />
      )}

      {mode !== null && isAgentEntered && consentStep === 'waiting' && agentEnteredIntakeId && (
        <AgentEnteredWaitingCard
          requestId={agentEnteredIntakeId}
          onDismiss={resetConsentFlow}
          onConsented={() => setConsentStep('health')}
          onConsentStatusChange={setConsentAccepted}
          consentedActionLabel="Continue"
        />
      )}

      {mode !== null && isAgentEntered && consentStep === 'health' && (
        <QuickPlanForm
          step="health"
          submitLabel={sendIntakeForm ? 'Create plan & send intake form' : undefined}
          defaultValues={identityValues ?? undefined}
          onSubmit={handleSubmit}
          isSubmitting={isPending}
          variant="dialog"
        />
      )}
    </>
  );

  if (!isMobile) {
    return (
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
      >
        <DialogContent
          className={cn('max-h-[90vh] max-w-lg overflow-y-auto transition-all duration-200', {
            'max-h-128 min-h-128 -translate-y-[calc(50%+1rem)] scale-[0.94]': showPartnerDialog,
          })}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DrawerContent
        className={cn('transition-all! duration-200! max-h-[90vh] overflow-y-auto px-4 pb-6', {
          'h-144 bottom-1! scale-[0.96]': showPartnerDialog,
        })}
      >
        <DrawerHeader className="text-left">
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>
        {content}
      </DrawerContent>
    </Drawer>
  );
}
